/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { IBiometricDevice } from "../../interfaces/BiometricDeviceInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import {
  getBiometricDevices,
  postBiometricDevice,
  patchBiometricDevice,
  deleteBiometricDevice
} from "../../api/biometricDevice";

export const biometricDevicesQueryKey = "biometricDevices";

// use biometric devices
export const useBiometricDevices = (): UseQueryResult<Array<IBiometricDevice>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [biometricDevicesQueryKey, resourceCentre?.id],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getBiometricDevices({
        resourceCentreId: Number(resourceCentre.id)
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post biometric device
export const usePostBiometricDevice = (): UseMutationResult<
  ApiResponse<IBiometricDevice>,
  Error,
  Partial<IBiometricDevice>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<IBiometricDevice>) => postBiometricDevice(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [biometricDevicesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Biometric device created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create biometric device",
          variant: "error"
        })
      );
    }
  });
};

// use patch biometric device
export const usePatchBiometricDevice = (): UseMutationResult<
  ApiResponse<IBiometricDevice>,
  Error,
  { id: number | string; data: Partial<IBiometricDevice> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchBiometricDevice({ id, data }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [biometricDevicesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Biometric device updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update biometric device",
          variant: "error"
        })
      );
    }
  });
};

// use delete biometric device
export const useDeleteBiometricDevice = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteBiometricDevice(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [biometricDevicesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Biometric device deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete biometric device",
          variant: "error"
        })
      );
    }
  });
};
